import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import {
  getLine,
  getStatement,
  listDrawdowns,
  listLines,
  listMovements,
  listStatements,
  type MovementFilter,
} from '../../api/credit.ts';
import { queryKeys } from '../../api/query-client.ts';

/** Línea principal del cliente (la primera; con varias líneas el selector llega en checkout). */
export function usePrimaryLine() {
  return useQuery({
    queryKey: queryKeys.lines,
    queryFn: listLines,
    select: (result) => result.data[0] ?? null,
  });
}

export function useLineDetail(lineId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.line(lineId ?? ''),
    queryFn: () => getLine(lineId ?? ''),
    enabled: lineId !== undefined,
  });
}

export function useDrawdowns(lineId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.drawdowns(lineId ?? ''),
    queryFn: () => listDrawdowns(lineId ?? ''),
    enabled: lineId !== undefined,
    select: (result) => result.data,
  });
}

export function useMovements(lineId: string | undefined, filter: MovementFilter, limit = 20) {
  return useInfiniteQuery({
    queryKey: [...queryKeys.movements(lineId ?? '', filter), limit],
    queryFn: ({ pageParam }) =>
      listMovements(lineId ?? '', { filter, cursor: pageParam ?? undefined, limit }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    enabled: lineId !== undefined,
  });
}

/**
 * Extractos de la línea (el más reciente primero) y el detalle del elegido (por defecto, el último
 * corte). El detalle trae la identificación legal de la entidad para la vista y el PDF.
 */
export function useStatements(lineId: string | undefined, selectedId?: string) {
  const list = useQuery({
    queryKey: queryKeys.statements(lineId ?? ''),
    queryFn: () => listStatements(lineId ?? ''),
    enabled: lineId !== undefined,
    select: (result) => [...result.data].sort((a, b) => b.cutoffDate.localeCompare(a.cutoffDate)),
  });
  const statementId =
    list.data?.find((s) => s.id === selectedId)?.id ?? list.data?.[0]?.id ?? undefined;
  const detail = useQuery({
    queryKey: queryKeys.statement(lineId ?? '', statementId ?? ''),
    queryFn: () => getStatement(lineId ?? '', statementId ?? ''),
    enabled: lineId !== undefined && statementId !== undefined,
  });
  return { list, detail };
}
